import React, { useEffect, useId, useRef, useState } from 'react';
import * as d3 from 'd3';
import { apiFetch } from '../../api/apiClient';

const HEIGHT = 280;
const MONTH_LETTERS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DEFAULT_Y_TICKS = Object.freeze([0, 1, 2, 3, 4, 5, 6]);
const BAR_COLOR = '#7B2CBF';
const Y_AXIS_LABEL_FONT_SIZE = '10px';
const Y_AXIS_LABEL_FONT_FAMILY = 'Arial, Helvetica, sans-serif';

function applyYAxisLabelStyle(selection) {
  return selection
    .style('font-size', Y_AXIS_LABEL_FONT_SIZE)
    .style('font-family', Y_AXIS_LABEL_FONT_FAMILY)
    .style('font-weight', 'bold')
    .attr('fill', '#000');
}

function monthlyLoss(year, month) {
  const seasonal = 0.55 + 5.05 * Math.exp(-Math.pow((month + 1 - 7.5) / 3.15, 2));
  const yearShift = 0.94 + 0.08 * Math.sin(year * 0.7);
  return Math.round(Math.min(6, Math.max(0.2, seasonal * yearShift)) * 10) / 10;
}

export function buildMonthlyAverageSoilingLoss(now = new Date()) {
  const endYear = now.getFullYear() - 1;
  const startYear = endYear - 9;
  const rows = [];

  for (let year = startYear; year <= endYear; year += 1) {
    for (let month = 0; month < 12; month += 1) {
      rows.push({
        key: `${year}-${String(month + 1).padStart(2, '0')}`,
        year,
        month,
        letter: MONTH_LETTERS[month],
        value: monthlyLoss(year, month),
      });
    }
  }

  return { startYear, endYear, rows };
}

/** Loss fraction 0–1 → display percent (1 decimal). */
export function lossFractionToPercent(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 1000) / 10;
}

export function mapMonthlySoilingPayload(payload) {
  const monthly = Array.isArray(payload?.monthly) ? payload.monthly : [];
  const byKey = new Map();

  for (const row of monthly) {
    const year = Number(row?.year);
    const month = Number(row?.month);
    if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
      continue;
    }
    const key = `${year}-${String(month).padStart(2, '0')}`;
    byKey.set(key, {
      key,
      year,
      month: month - 1,
      letter: MONTH_LETTERS[month - 1],
      value: lossFractionToPercent(row.avgKimberLossFraction),
    });
  }

  if (byKey.size === 0) {
    return { startYear: null, endYear: null, rows: [] };
  }

  const years = [...byKey.values()].map((d) => d.year);
  const startYear = Math.min(...years);
  const endYear = Math.max(...years);
  const rows = [];

  for (let year = startYear; year <= endYear; year += 1) {
    for (let month = 0; month < 12; month += 1) {
      const key = `${year}-${String(month + 1).padStart(2, '0')}`;
      rows.push(
        byKey.get(key) ?? {
          key,
          year,
          month,
          letter: MONTH_LETTERS[month],
          value: null,
        },
      );
    }
  }

  return { startYear, endYear, rows };
}

function buildYTicks(rows) {
  const dataMax = d3.max(rows, (d) => (d.value == null ? null : d.value));
  if (dataMax == null || dataMax <= 6) {
    return DEFAULT_Y_TICKS;
  }
  const yMax = Math.ceil(dataMax);
  const ticks = d3.ticks(0, yMax, 6);
  if (ticks[0] !== 0) ticks.unshift(0);
  if (ticks[ticks.length - 1] < yMax) ticks.push(yMax);
  return ticks;
}

const EMPTY_HISTORY = Object.freeze({ startYear: null, endYear: null, rows: [] });

export default function MonthlyAverageSoilingLoss({ onTooltip, portfolioName = '' }) {
  const [history, setHistory] = useState(EMPTY_HISTORY);
  const wrapperRef = useRef(null);
  const svgRef = useRef(null);
  const onTooltipRef = useRef(onTooltip);
  onTooltipRef.current = onTooltip;
  const reactId = useId().replace(/:/g, '');
  const [dimensions, setDimensions] = useState({ width: 800, height: HEIGHT });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!portfolioName) {
        setHistory(EMPTY_HISTORY);
        return;
      }

      try {
        const payload = await apiFetch(`/api/soiling/portfolio/${encodeURIComponent(portfolioName)}/monthly`);
        if (!cancelled) {
          setHistory(mapMonthlySoilingPayload(payload));
        }
      } catch (err) {
        console.warn('Monthly soiling profile API fetch failed:', err);
        if (!cancelled) {
          setHistory(EMPTY_HISTORY);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [portfolioName]);

  useEffect(() => {
    if (!wrapperRef.current) return undefined;
    const el = wrapperRef.current;
    const update = () => {
      const { width } = el.getBoundingClientRect();
      setDimensions({ width: Math.max(0, Math.floor(width)), height: HEIGHT });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    el.addEventListener('transitionend', update);
    return () => {
      ro.disconnect();
      el.removeEventListener('transitionend', update);
    };
  }, []);

  useEffect(() => {
    if (!svgRef.current || !history.rows.length) return undefined;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const { width, height } = dimensions;
    const margin = { top: 16, right: 48, bottom: 52, left: 16 };
    const plotLeft = margin.left;
    const plotRight = width - margin.right;
    const plotTop = margin.top;
    const plotBottom = height - margin.bottom;
    const plotW = Math.max(0, plotRight - plotLeft);
    const rows = history.rows;

    const x = d3
      .scaleBand()
      .domain(rows.map((d) => d.key))
      .range([plotLeft, plotRight])
      .paddingInner(0.22)
      .paddingOuter(0.04);

    const yTicks = buildYTicks(rows);
    const yMax = yTicks[yTicks.length - 1] ?? 6;
    const y = d3.scaleLinear().domain([0, yMax]).range([plotBottom, plotTop]);
    const barW = x.bandwidth();

    yTicks.forEach((value) => {
      svg
        .append('line')
        .attr('x1', plotLeft)
        .attr('x2', plotRight)
        .attr('y1', y(value))
        .attr('y2', y(value))
        .attr('stroke', value === 0 ? '#000' : '#d9d9d9')
        .attr('stroke-width', value === 0 ? 1 : 1);
    });

    const yAxis = d3
      .axisRight(y)
      .tickValues(yTicks)
      .tickPadding(10)
      .tickSizeInner(0)
      .tickFormat((d) => `${d}%`);

    applyYAxisLabelStyle(
      svg.append('g').attr('transform', `translate(${plotRight},0)`).call(yAxis).selectAll('text'),
    );

    const xAxisGroup = svg.append('g').attr('transform', `translate(0,${plotBottom})`);
    xAxisGroup
      .append('line')
      .attr('x1', plotLeft)
      .attr('x2', plotRight)
      .attr('stroke', '#000');

    rows.forEach((d) => {
      const cx = (x(d.key) ?? 0) + barW / 2;
      xAxisGroup
        .append('text')
        .attr('x', cx)
        .attr('y', 14)
        .attr('text-anchor', 'middle')
        .style('font-size', '9px')
        .style('font-weight', 'bold')
        .text(d.letter);
    });

    for (let year = history.startYear; year <= history.endYear; year += 1) {
      const first = x(`${year}-01`);
      const last = x(`${year}-12`);
      if (first == null || last == null) continue;
      const cx = (first + last + barW) / 2;
      xAxisGroup
        .append('text')
        .attr('x', cx)
        .attr('y', 32)
        .attr('text-anchor', 'middle')
        .style('font-size', '11px')
        .style('font-weight', 'bold')
        .text(year);
    }

    const gradId = `monthly-soiling-grad-${reactId}`;
    const grad = svg.append('defs').append('linearGradient')
      .attr('id', gradId)
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '0%')
      .attr('y2', '100%');
    grad.append('stop').attr('offset', '0%').attr('stop-color', BAR_COLOR);
    grad.append('stop').attr('offset', '100%').attr('stop-color', '#000');

    svg
      .selectAll('.soiling-bar')
      .data(rows)
      .enter()
      .append('rect')
      .attr('class', 'soiling-bar')
      .attr('x', (d) => x(d.key) ?? 0)
      .attr('width', barW)
      .attr('y', (d) => (d.value == null ? y(0) : y(d.value)))
      .attr('height', (d) => (d.value == null ? 0 : Math.max(0, y(0) - y(d.value))))
      .attr('fill', `url(#${gradId})`)
      .style('pointer-events', 'none');

    const nearestIndexByPx = (px) => {
      let best = 0;
      let bestDx = Infinity;
      rows.forEach((d, i) => {
        const cx = (x(d.key) ?? 0) + barW / 2;
        const dx = Math.abs(cx - px);
        if (dx < bestDx) {
          bestDx = dx;
          best = i;
        }
      });
      return best;
    };

    const showBar = (idx) => {
      const d = rows[Math.max(0, Math.min(rows.length - 1, idx))];
      const tip = onTooltipRef.current;
      const svgEl = svgRef.current;
      if (!tip || !svgEl || !d || d.value == null) {
        hideBar();
        return;
      }
      const rect = svgEl.getBoundingClientRect();
      const cx = (x(d.key) ?? 0) + barW / 2;
      tip({
        visible: true,
        content: (
          <>
            <strong>{MONTH_NAMES[d.month]} {d.year}</strong>
            <div>Average soiling loss (Kimber): {d.value.toFixed(1)}%</div>
          </>
        ),
        x: rect.left + cx,
        y: rect.top + y(d.value) - 8,
      });
    };

    const hideBar = () => {
      const tip = onTooltipRef.current;
      if (tip) tip((t) => ({ ...t, visible: false }));
    };

    svg
      .append('rect')
      .attr('x', plotLeft)
      .attr('y', plotTop)
      .attr('width', plotW)
      .attr('height', Math.max(0, plotBottom - plotTop))
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair')
      .on('pointermove', (event) => {
        showBar(nearestIndexByPx(d3.pointer(event, svgRef.current)[0]));
      })
      .on('pointerleave', hideBar);

    return undefined;
  }, [dimensions, history, reactId]);

  return (
    <div
      ref={wrapperRef}
      className="monthly-average-soiling-loss"
      style={{ width: '100%', height: 'auto', minWidth: 0 }}
    >
      <svg
        ref={svgRef}
        role="img"
        aria-label="Monthly average soiling loss over the previous 10 years."
        style={{ width: '100%', height: `${HEIGHT}px`, display: 'block' }}
      />
    </div>
  );
}
